/**
 * Response Helper Utilities
 * Helper functions for extracting data from API responses
 * Handles various response formats from the backend
 */

/**
 * Extract the actual data from API response
 * Handles multiple response formats:
 * 1. { status: true, data: [...] } -> returns [...]
 * 2. { data: [...] } -> returns [...]
 * 3. [...] -> returns [...]
 * 4. response.data exists -> returns response.data
 */
export function extractDataFromResponse(response: any): any {
  // If response is null or undefined
  if (!response) {
    return null;
  }

  // If response is already an array, return it
  if (Array.isArray(response)) {
    return response;
  }

  // If response has a data property
  if (response.data !== undefined) {
    // If data is an array, return it
    if (Array.isArray(response.data)) {
      return response.data;
    }
    // If data is an object with its own data property, recurse
    if (response.data && typeof response.data === 'object' && response.data.data !== undefined) {
      return extractDataFromResponse(response.data);
    }
    // Return the data property
    return response.data;
  }

  // If response is an object but no data property, return the response itself
  if (typeof response === 'object') {
    return response;
  }

  // Fallback: return response as-is
  return response;
}

/**
 * Extract array data from API response (specifically for list endpoints)
 */
export function extractArrayFromResponse(response: any): any[] {
  const data = extractDataFromResponse(response);
  
  if (Array.isArray(data)) {
    return data;
  }
  
  // If data is not an array but exists, wrap it
  if (data) {
    return [data];
  }
  
  // Default to empty array
  return [];
}

